import { forwardRef, useId, useState, type InputHTMLAttributes } from 'react';
import { Eye, EyeOff } from 'lucide-react';

type PasswordInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>;

/**
 * Shared password field with a show/hide toggle. A drop-in replacement for
 * `<input type="password">`: every other prop (value, onChange, minLength,
 * autoComplete, placeholder, required, …) is forwarded to the input untouched,
 * so validation and submission behave exactly as before.
 *
 * Each instance owns its own visibility state — revealing "Password" never
 * reveals "Confirm password". The toggle is a real `type="button"` (never
 * submits the form), is keyboard-operable, and announces its action to
 * assistive tech. The wrapper is `display:block` and the input keeps its
 * global styling, only gaining right padding so text never runs under the icon.
 */
const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(function PasswordInput(
  { className, id, ...rest },
  ref,
) {
  const [visible, setVisible] = useState(false);
  const fallbackId = useId();
  const inputId = id ?? `password-${fallbackId}`;

  return (
    <span className="password-field">
      <input
        {...rest}
        ref={ref}
        id={inputId}
        type={visible ? 'text' : 'password'}
        className={className ? `password-field-input ${className}` : 'password-field-input'}
      />
      <button
        type="button"
        className="password-toggle"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? 'Hide password' : 'Show password'}
        aria-pressed={visible}
        aria-controls={inputId}
        title={visible ? 'Hide password' : 'Show password'}
        data-testid="button-toggle-password"
      >
        {visible ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
      </button>
    </span>
  );
});

export default PasswordInput;
